import PropTypes from "prop-types";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import SvgIcon from "../../../../parts/SvgIcon";
import Toast from "../../../../parts/toast";
import ToolTip from "../../../../parts/tooltip-portal";
import MyFitnessPalDiaryInfoModal from "./MyFitnessPalDiaryInfoModal";

const EXTERNAL_LINK_ICON = "/img/new-icons/export.svg";
const ICON_SIZE = { width: "0.875rem", height: "0.875rem" };
const LOCK_ICON = "/img/new-icons/lock.svg";
const MFP_ORIGIN = "https://www.myfitnesspal.com/";
const NOT_SHARED = "not_shared";

const COPY_INFO_SEEN_KEY = "mfpDiaryCopyInfoShown";

const markCopyInfoSeen = () =>
  fetch("/actions/user/video-shown", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ type: COPY_INFO_SEEN_KEY }),
  }).catch(() => {});

function copyWithinGesture(text) {
  const span = document.createElement("span");
  span.textContent = text;
  span.style.whiteSpace = "pre";
  span.style.webkitUserSelect = "auto";
  span.style.userSelect = "all";
  document.body.appendChild(span);

  const selection = window.getSelection();
  const range = document.createRange();
  selection.removeAllRanges();
  range.selectNode(span);
  selection.addRange(range);

  let copied = false;
  try {
    copied = document.execCommand("copy");
  } catch {
    copied = false;
  }
  selection.removeAllRanges();
  span.remove();

  if (copied) return Promise.resolve();
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  return Promise.reject(new Error("Clipboard unavailable"));
}

const MyFitnessPalDiaryLink = ({ athleteId, activeUser }) => {
  const { t } = useTranslation("athlete-portal");
  const [diary, setDiary] = useState(null);
  const [toast, setToast] = useState(null);
  const [copyInfoSeen, setCopyInfoSeen] = useState(() =>
    Boolean(activeUser?.welcomeVideos?.[COPY_INFO_SEEN_KEY])
  );
  const [copyInfoOpen, setCopyInfoOpen] = useState(false);
  const toastTimerRef = useRef(null);
  const descriptionId = useId();

  useEffect(() => {
    setDiary(null);
    if (!athleteId) return undefined;

    const controller = new AbortController();
    fetch(
      `/actions/myfitnesspal-diary?athleteId=${encodeURIComponent(athleteId)}`,
      {
        credentials: "same-origin",
        cache: "no-store",
        signal: controller.signal,
      }
    )
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (data?.available || data?.reason === NOT_SHARED) {
          setDiary({ ...data, athleteId });
        }
      })
      .catch(() => {});

    return () => controller.abort();
  }, [athleteId]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  // A response for a previous athlete (switch mid-fetch) must never be shown.
  if (!diary || diary.athleteId !== athleteId) return null;

  if (diary.reason === NOT_SHARED) {
    return (
      <span className="np-mfp-diary">
        <ToolTip
          body={t("nutritionPage.mfpDiary.lockedTooltip")}
          position="bottom"
        >
          <button
            type="button"
            className="np-mfp-diary-link np-mfp-diary-link--locked"
            aria-disabled="true"
            aria-describedby={descriptionId}
          >
            <SvgIcon
              src={LOCK_ICON}
              useCurrentColor
              aria-hidden="true"
              style={ICON_SIZE}
            />
            <span>{t("nutritionPage.mfpDiary.link")}</span>
          </button>
        </ToolTip>
        <span id={descriptionId} className="sr-only">
          {t("nutritionPage.mfpDiary.lockedTooltip")}
        </span>
      </span>
    );
  }

  // Never copy a key or open a URL from an unexpected response.
  if (!diary.diaryKey || !diary.diaryUrl?.startsWith(MFP_ORIGIN)) return null;

  const showToast = (type, message) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(
      () => setToast(null),
      type === "error" ? 10000 : 5000
    );
  };

  // Copy first, synchronously: must stay inside the click that opens the tab.
  const handleOpen = () => {
    const { diaryKey } = diary;
    copyWithinGesture(diaryKey).then(
      () => showToast("success", t("nutritionPage.mfpDiary.copied")),
      // Appended outside t() so interpolation escaping can't mangle it.
      () =>
        showToast(
          "error",
          `${t("nutritionPage.mfpDiary.copyFailed")} ${diaryKey}`
        )
    );
  };

  const dismissCopyInfo = () => {
    setCopyInfoOpen(false);
    setCopyInfoSeen(true);
    markCopyInfoSeen();
  };

  const handleLinkClick = (event) => {
    if (!copyInfoSeen) {
      event.preventDefault();
      setCopyInfoOpen(true);
      return;
    }
    handleOpen();
  };

  const handleAuxClick = (event) => {
    if (event.button === 1) handleLinkClick(event);
  };

  const handleOpenFromInfo = () => {
    handleOpen();
    dismissCopyInfo();
  };

  return (
    <span className="np-mfp-diary">
      <ToolTip body={t("nutritionPage.mfpDiary.tooltip")} position="bottom">
        <a
          className="np-mfp-diary-link"
          href={diary.diaryUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleLinkClick}
          onAuxClick={handleAuxClick}
          aria-describedby={descriptionId}
        >
          <span>{t("nutritionPage.mfpDiary.link")}</span>
          <SvgIcon
            src={EXTERNAL_LINK_ICON}
            useCurrentColor
            aria-hidden="true"
            style={ICON_SIZE}
          />
          <span className="sr-only">
            {t("nutritionPage.mfpDiary.opensInNewTab")}
          </span>
        </a>
      </ToolTip>
      <span id={descriptionId} className="sr-only">
        {t("nutritionPage.mfpDiary.tooltip")}
      </span>
      <output aria-live="polite" className="sr-only">
        {toast?.message ?? ""}
      </output>
      {copyInfoOpen && (
        <MyFitnessPalDiaryInfoModal
          diaryUrl={diary.diaryUrl}
          onOpen={handleOpenFromInfo}
          onClose={dismissCopyInfo}
        />
      )}
      <Toast
        open={Boolean(toast)}
        type={toast?.type ?? "success"}
        message={toast?.message ?? ""}
      />
    </span>
  );
};

MyFitnessPalDiaryLink.propTypes = {
  athleteId: PropTypes.string,
  activeUser: PropTypes.shape({
    welcomeVideos: PropTypes.objectOf(PropTypes.bool),
  }),
};

export default MyFitnessPalDiaryLink;
